<template>
  <div :class="$style.root">
    <AtomsConsumerLink
      v-if="imgList.length === 1"
      :url="imgList[0].url"
      :class="$style.picture_one"
      @click="
        trackBannerClick(
          'image_slide_big',
          !minScreenMd ? imgList[0].sp_img_url ?? '' : imgList[0].pc_img_url ?? '',
          0,
          !minScreenMd ? imgList[0].sp_img_url_caption ?? '' : imgList[0].pc_img_url_caption ?? '',
        )
      "
    >
      <AtomsConsumerImage
        v-if="isTop"
        :src="(!minScreenMd ? imgList[0].sp_img_url : imgList[0].pc_img_url) ?? ''"
        :width="!minScreenMd ? 375 : 1320"
        :height="!minScreenMd ? 660 : 680"
        :quality="0.7"
        :alt="(!minScreenMd ? imgList[0].sp_img_url_caption : imgList[0].pc_img_url_caption) ?? ''"
        :class="$style.isTop"
        @contextmenu.prevent
        @dragstart.prevent
      />
      <AtomsConsumerImage
        v-else
        :src="(!minScreenMd ? imgList[0].sp_img_url : imgList[0].pc_img_url) ?? ''"
        :width="!minScreenMd ? 375 : 1220"
        :height="!minScreenMd ? 450 : 620"
        :quality="0.7"
        :alt="(!minScreenMd ? imgList[0].sp_img_url_caption : imgList[0].pc_img_url_caption) ?? ''"
      />
    </AtomsConsumerLink>
    <template v-else>
      <div ref="carousel" :class="$style.carousel">
        <div :class="$style.container">
          <AtomsConsumerLink
            v-for="(img, index) in imgList"
            :key="index"
            :url="img.url"
            :class="$style.slide"
            @click="
              trackBannerClick(
                'image_slide_big',
                !minScreenMd ? img.sp_img_url ?? '' : img.pc_img_url ?? '',
                index,
                !minScreenMd ? img.sp_img_url_caption ?? '' : img.pc_img_url_caption ?? '',
              )
            "
          >
            <AtomsConsumerImage
              v-if="isTop"
              :src="(!minScreenMd ? img.sp_img_url : img.pc_img_url) ?? ''"
              :width="!minScreenMd ? 375 : 1320"
              :height="!minScreenMd ? 660 : 680"
              :quality="0.7"
              :alt="(!minScreenMd ? img.sp_img_url_caption : img.pc_img_url_caption) ?? ''"
              :priority="index === 0 ? 'high' : 'auto'"
              :lazy="index !== 0"
              :class="$style.isTop"
              @contextmenu.prevent
              @dragstart.prevent
            />
            <AtomsConsumerImage
              v-else
              :src="(!minScreenMd ? img.sp_img_url : img.pc_img_url) ?? ''"
              :width="!minScreenMd ? 375 : 1220"
              :height="!minScreenMd ? 450 : 620"
              :quality="0.7"
              :alt="(!minScreenMd ? img.sp_img_url_caption : img.pc_img_url_caption) ?? ''"
              :priority="index === 0 ? 'high' : 'auto'"
              :lazy="index !== 0"
            />
          </AtomsConsumerLink>
        </div>
      </div>
      <div v-if="minScreenMd" :class="$style.buttons">
        <AtomsConsumerButton :class="[$style.button, $style.prev]" @click="emblaApi?.scrollPrev()" />
        <AtomsConsumerButton :class="$style.button" @click="emblaApi?.scrollNext()" />
      </div>
      <div :class="$style.dots">
        <div
          v-for="(_, index) in imgList"
          :key="index"
          :class="[$style.dot, { [$style.active]: dotsSelectedIndex === index }]"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import emblaCarouselVue from "embla-carousel-vue"
import Autoplay from "embla-carousel-autoplay"
import type { PagePartsFragmentFragment } from "~/types/pageParts"

type ImgType = Pick<
  PagePartsFragmentFragment["list"][0],
  "url" | "sp_img_url" | "pc_img_url" | "pc_img_url_caption" | "sp_img_url_caption"
>
defineProps<{
  imgList: ImgType[]
  /**
   * トップページのスワイパーか
   * - トップの場合はアスペクト比が変わり、画像の保存・印刷を不可にする
   */
  isTop?: boolean
}>()

const [carousel, emblaApi] = emblaCarouselVue({ loop: true }, [Autoplay({ delay: 4000 })])

const { trackBannerClick } = useGtagEvent()

const { width: windowWidth } = useWindowSize()
const minScreenMd = computed(() => windowWidth.value >= 768)

const dotsSelectedIndex = ref<number>(0)
watchEffect(() => {
  if (emblaApi.value) {
    // 初回のインデックスを設定
    dotsSelectedIndex.value = emblaApi.value.selectedScrollSnap()

    // autoplay, 手動でスライドを変更した際イベントをハンドリング -> インデックス更新
    emblaApi.value.on("select", (event) => {
      dotsSelectedIndex.value = event.selectedScrollSnap()
    })
  }
})
</script>

<style scoped module lang="scss">
.root {
  position: relative;

  .picture_one {
    width: 100%;
    aspect-ratio: 375 / 450;
    &.isTop {
      aspect-ratio: 375 / 660;
      @media print {
        visibility: hidden !important;
      }
    }
    &:hover {
      opacity: inherit;
    }
    img {
      width: 100%;
      object-fit: cover;
      @include background-skeleton-loading;
    }
  }

  .carousel {
    overflow: hidden;

    .container {
      display: flex;
      .slide {
        flex: 0 0 100%;
        cursor: pointer;
        img {
          width: 100%;
          object-fit: cover;
          aspect-ratio: 375 / 450;
          &.isTop {
            aspect-ratio: 375 / 660;
            @media print {
              visibility: hidden !important;
            }
          }
        }
        @include md {
          flex: 0 0 90%;
          img {
            aspect-ratio: 1220 / 620;
            border-radius: 0.5rem;
            &.isTop {
              aspect-ratio: 1320 / 680;
            }
          }
        }
        @include lg {
          flex: 0 0 80%;
        }
      }
      @include md {
        gap: 0.5rem;

        .slide {
          &:last-child {
            margin-right: 0.5rem;
          }
        }
      }
    }
  }

  .buttons {
    @include md {
      position: absolute;
      top: 50%;
      width: 100%;
      display: flex;
      justify-content: space-between;
      padding: 0 3.5rem;
      pointer-events: none;

      .button {
        display: block;
        transform: translateY(-50%);
        width: 56px;
        height: 56px;
        background: url("/images/common/arrow/arrow-secondary.svg") no-repeat center $white;
        border-radius: 50%;
        z-index: 1;
        pointer-events: auto;
        opacity: 0.5;

        &.prev {
          transform: translateY(-50%) scale(-1, 1);
        }
      }
    }
  }

  .dots {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1.25rem;

    .dot {
      width: 3rem;
      height: 0.125rem;
      background-color: $primary-20;
      border-radius: 4px;

      @include md {
        width: 3.75rem;
      }

      &.active {
        background-color: $primary;
      }
    }
  }
}
</style>
